// =============================================================================
// Module Project - UI: NotificationBell
// Cloche de notifications avec badge unread + dropdown liste
// Affiche les notifications projet, chat ET documents
// =============================================================================

import React, { useEffect, useRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Bell, Check, CheckCheck, Trash2, AtSign, FileText, MessageSquare, Inbox,
  UserPlus, UserMinus, UserCheck, MessageCircle, ArrowRightCircle, CheckCircle2,
  Ban, Unlock, Paperclip, Eye, CalendarClock, AlarmClock, ClipboardPlus,
  ClipboardX, ListChecks, Flag, Trophy, Users, ShieldCheck, ThumbsUp, ThumbsDown,
  Send, Flame, Gauge, BookOpen,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useNotificationsStore } from '../hooks/useNotificationsStore';
import { formatNotificationMessage } from '../data/notificationRepository';
import {
  fetchChatNotifications,
  countUnreadChatNotifications,
  markChatNotificationRead,
  markAllChatNotificationsRead,
  deleteChatNotification,
  type ChatNotification,
} from '@/modules/chat/data/chatNotificationRepository';
import {
  fetchDocNotifications,
  countUnreadDocNotifications,
  markDocNotificationRead,
  markAllDocNotificationsRead,
  deleteDocNotification,
  type DocNotification,
} from '@/modules/documents/data/docNotificationRepository';
import { subscribeToUnifiedNotificationChanges } from '../data/unifiedNotificationSubscriptionService';

// Unified notification type for display
interface UnifiedNotification {
  id: string;
  source: 'project' | 'chat' | 'doc';
  message: string;
  isRead: boolean;
  createdAt: string;
  // Type brut (notif projet) ou marqueur de source pour mapper icône/couleur
  kind: string;
  // Navigation
  projectId?: string;
  entityId?: string;
  channelId?: string;
  documentId?: string;
  pageId?: string;
  isWikiDoc?: boolean;
}

// Mapping centralisé : type de notification → icône + palette de couleur
type IconMeta = { Icon: LucideIcon; tone: string };

function getNotificationVisual(kind: string): IconMeta {
  switch (kind) {
    // — Affectations / membres
    case 'assign':                return { Icon: UserPlus,        tone: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' };
    case 'unassign':              return { Icon: UserMinus,       tone: 'bg-slate-500/10 text-slate-600 dark:text-slate-400' };
    case 'owner_change':          return { Icon: UserCheck,       tone: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' };
    case 'watcher_added':         return { Icon: Eye,             tone: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' };
    case 'project_member_added':  return { Icon: Users,           tone: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' };
    case 'project_member_removed':return { Icon: Users,           tone: 'bg-slate-500/10 text-slate-600 dark:text-slate-400' };
    case 'project_role_changed':  return { Icon: ShieldCheck,     tone: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' };

    // — Mentions & commentaires
    case 'mention':
    case 'chat_mention':          return { Icon: AtSign,          tone: 'bg-rose-500/10 text-rose-600 dark:text-rose-400' };
    case 'comment':               return { Icon: MessageCircle,   tone: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' };
    case 'chat_message':          return { Icon: MessageSquare,   tone: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' };

    // — Statut / progression
    case 'status_change':         return { Icon: ArrowRightCircle,tone: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' };
    case 'status_terminal':       return { Icon: CheckCircle2,    tone: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' };
    case 'subtask_completed':     return { Icon: ListChecks,      tone: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' };

    // — Dépendances
    case 'dependency_blocked':    return { Icon: Ban,             tone: 'bg-red-500/10 text-red-600 dark:text-red-400' };
    case 'dependency_resolved':   return { Icon: Unlock,          tone: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' };

    // — Pièces jointes
    case 'attachment_uploaded':   return { Icon: Paperclip,       tone: 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400' };

    // — Échéance
    case 'due_date_changed':      return { Icon: CalendarClock,   tone: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' };
    case 'due_date_approaching':  return { Icon: AlarmClock,      tone: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' };

    // — Tâches
    case 'task_created':          return { Icon: ClipboardPlus,   tone: 'bg-teal-500/10 text-teal-600 dark:text-teal-400' };
    case 'task_deleted':          return { Icon: ClipboardX,      tone: 'bg-red-500/10 text-red-600 dark:text-red-400' };

    // — Jalons
    case 'milestone_assigned':    return { Icon: Flag,            tone: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' };
    case 'milestone_completed':   return { Icon: Trophy,          tone: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-500' };

    // — Timesheet
    case 'timesheet_submitted':   return { Icon: Send,            tone: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' };
    case 'timesheet_approved':    return { Icon: ThumbsUp,        tone: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' };
    case 'timesheet_rejected':    return { Icon: ThumbsDown,      tone: 'bg-red-500/10 text-red-600 dark:text-red-400' };

    // — Priorité / estimation
    case 'priority_changed':      return { Icon: Flame,           tone: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' };
    case 'estimation_changed':    return { Icon: Gauge,           tone: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' };

    // — Documents / wiki
    case 'doc_mention':           return { Icon: AtSign,          tone: 'bg-rose-500/10 text-rose-600 dark:text-rose-400' };
    case 'wiki_mention':          return { Icon: BookOpen,        tone: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' };
    case 'doc':                   return { Icon: FileText,        tone: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' };

    default:                      return { Icon: Bell,            tone: 'bg-primary/10 text-primary' };
  }
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const [open, setOpen] = React.useState(false);
  
  const {
    notifications: projectNotifications,
    unreadCount: projectUnreadCount,
    loading: projectLoading,
    loadNotifications: loadProjectNotifications,
    refreshUnreadCount: refreshProjectUnreadCount,
    markNotificationAsRead: markProjectRead,
    markAllNotificationsAsRead: markAllProjectRead,
    removeNotification: removeProjectNotification,
  } = useNotificationsStore(currentOrganization?.id);

  // Chat notifications state
  const [chatNotifications, setChatNotifications] = React.useState<ChatNotification[]>([]);
  const [chatUnreadCount, setChatUnreadCount] = React.useState(0);
  const [chatLoading, setChatLoading] = React.useState(false);

  // Doc notifications state
  const [docNotifications, setDocNotifications] = React.useState<DocNotification[]>([]);
  const [docUnreadCount, setDocUnreadCount] = React.useState(0);

  // Total unread
  const totalUnread = projectUnreadCount + chatUnreadCount + docUnreadCount;

  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  const refreshChatNotifications = React.useCallback(async () => {
    const [notifications, unread] = await Promise.all([
      fetchChatNotifications(30),
      countUnreadChatNotifications(),
    ]);
    setChatNotifications(notifications);
    setChatUnreadCount(unread);
  }, []);

  const refreshDocNotifications = React.useCallback(async () => {
    if (!currentOrganization?.id) return;
    const [notifications, unread] = await Promise.all([
      fetchDocNotifications(currentOrganization.id, 30),
      countUnreadDocNotifications(currentOrganization.id),
    ]);
    setDocNotifications(notifications);
    setDocUnreadCount(unread);
  }, [currentOrganization?.id]);

  const refreshAllNotifications = React.useCallback(async () => {
    if (!currentOrganization?.id) return;
    await Promise.all([
      openRef.current ? loadProjectNotifications() : refreshProjectUnreadCount(),
      refreshChatNotifications(),
      refreshDocNotifications(),
    ]);
  }, [currentOrganization?.id, loadProjectNotifications, refreshProjectUnreadCount, refreshChatNotifications, refreshDocNotifications]);

  useEffect(() => {
    if (currentOrganization?.id) {
      refreshAllNotifications();
    }
  }, [currentOrganization?.id, refreshAllNotifications]);

  // Solution radicale : realtime unifié + polling court + refresh au retour d'onglet.
  // Le polling sert de filet de sécurité si le socket realtime rate un event RLS.
  useEffect(() => {
    if (!currentOrganization?.id) return;
    const orgId = currentOrganization.id;
    let timeoutId: number | undefined;
    const scheduleRefresh = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(refreshAllNotifications, 250);
    };

    const unsubscribe = subscribeToUnifiedNotificationChanges(orgId, scheduleRefresh);
    // Batch 3 (B3.2): polling reduced from 3s to 30s. Realtime channels
    // (notifications / chat_notifications / docs_notifications) remain the
    // primary path; focus + visibilitychange below give immediate refresh on
    // tab return. This interval is only a fallback when realtime is down.
    const intervalId = window.setInterval(refreshAllNotifications, 30_000);
    const handleFocus = () => refreshAllNotifications();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshAllNotifications();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      unsubscribe();
    };
  }, [currentOrganization?.id, refreshAllNotifications]);

  // Load full notifications when dropdown opens
  useEffect(() => {
    if (currentOrganization?.id && open) {
      loadProjectNotifications();
      setChatLoading(true);
      fetchChatNotifications(30).then(data => {
        setChatNotifications(data);
        setChatLoading(false);
      });
      refreshDocNotifications();
    }
  }, [currentOrganization?.id, open, loadProjectNotifications, refreshDocNotifications]);

  // Build unified list sorted by date
  const unified: UnifiedNotification[] = React.useMemo(() => {
    const fromProject: UnifiedNotification[] = projectNotifications.map(n => ({
      id: n.id,
      source: 'project' as const,
      message: formatNotificationMessage(n),
      isRead: n.isRead,
      createdAt: n.createdAt,
      kind: n.type,
      projectId: n.projectId,
      entityId: n.entityId,
    }));

    const fromChat: UnifiedNotification[] = chatNotifications.map(n => ({
      id: n.id,
      source: 'chat' as const,
      message: `${n.senderName || 'Quelqu\'un'} vous a mentionné dans #${n.channelName || 'Discussion'}`,
      isRead: n.isRead,
      createdAt: n.createdAt,
      kind: 'chat_mention',
      channelId: n.channelId,
    }));

    const fromDoc: UnifiedNotification[] = docNotifications.map(n => ({
      id: n.id,
      source: 'doc' as const,
      message: n.messagePreview || `${n.senderName || 'Quelqu\'un'} vous a mentionné dans "${n.documentTitle || 'un document'}"`,
      isRead: n.isRead,
      createdAt: n.createdAt,
      kind: n.type === 'wiki_mention' ? 'wiki_mention' : 'doc_mention',
      documentId: n.documentId,
      pageId: n.pageId,
      isWikiDoc: n.type === 'wiki_mention',
    }));

    return [...fromProject, ...fromChat, ...fromDoc].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [projectNotifications, chatNotifications, docNotifications]);

  const handleClick = (notif: UnifiedNotification) => {
    if (!notif.isRead) {
      if (notif.source === 'project') {
        markProjectRead(notif.id);
      } else if (notif.source === 'chat') {
        markChatNotificationRead(notif.id).then(() => {
          refreshChatNotifications();
        });
      } else if (notif.source === 'doc') {
        markDocNotificationRead(notif.id).then(() => {
          setDocNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
          setDocUnreadCount(prev => Math.max(0, prev - 1));
        });
      }
    }
    setOpen(false);
    if (notif.source === 'project' && notif.projectId && notif.entityId) {
      navigate(`/projects/${notif.projectId}?task=${notif.entityId}`);
    } else if (notif.source === 'chat' && notif.channelId) {
      navigate('/discussion');
    } else if (notif.source === 'doc' && notif.documentId) {
      const base = notif.isWikiDoc ? '/wiki' : '/documents';
      const url = notif.pageId
        ? `${base}/${notif.documentId}?page=${notif.pageId}`
        : `${base}/${notif.documentId}`;
      navigate(url);
    }
  };

  const handleMarkAllRead = async () => {
    markAllProjectRead();
    await markAllChatNotificationsRead();
    await refreshChatNotifications();
    // Mark all doc notifications read
    if (currentOrganization?.id) {
      await markAllDocNotificationsRead(currentOrganization.id);
      setDocNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setDocUnreadCount(0);
    }
  };

  const handleDelete = (notif: UnifiedNotification) => {
    if (notif.source === 'project') {
      removeProjectNotification(notif.id);
    } else if (notif.source === 'chat') {
      deleteChatNotification(notif.id).then(() => {
        refreshChatNotifications();
      });
    } else if (notif.source === 'doc') {
      deleteDocNotification(notif.id).then(() => {
        const wasUnread = docNotifications.find(n => n.id === notif.id && !n.isRead);
        setDocNotifications(prev => prev.filter(n => n.id !== notif.id));
        if (wasUnread) setDocUnreadCount(prev => Math.max(0, prev - 1));
      });
    }
  };

  const loading = projectLoading || chatLoading;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 relative text-muted-foreground overflow-visible">
          <Bell className="h-3.5 w-3.5" />
          {totalUnread > 0 && (
            <Badge
              variant="destructive"
              className="absolute top-0 -right-0.5 h-3.5 min-w-3.5 flex items-center justify-center p-0 px-0.5 text-[8px] leading-none z-50 pointer-events-none"
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[380px] p-0 overflow-hidden rounded-xl border-border/60 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-gradient-to-b from-muted/30 to-transparent">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-[15px] tracking-tight">Notifications</h3>
            {totalUnread > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold tabular-nums">
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </div>
          {totalUnread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={handleMarkAllRead}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Tout marquer lu
            </Button>
          )}
        </div>

        <ScrollArea className="h-[440px]">
          {loading ? (
            <div className="p-3 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-2">
                  <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : unified.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <div className="h-12 w-12 rounded-full bg-muted/60 flex items-center justify-center mb-3">
                <Inbox className="h-5 w-5 text-muted-foreground/70" strokeWidth={1.75} />
              </div>
              <p className="text-sm font-medium text-foreground">Tout est à jour</p>
              <p className="text-xs text-muted-foreground mt-1">Aucune notification pour le moment</p>
            </div>
          ) : (
            <div className="py-1">
              {unified.map((notif) => {
                const { Icon: SourceIcon, tone } = getNotificationVisual(notif.kind);
                return (
                <div
                  key={`${notif.source}-${notif.id}`}
                  className={cn(
                    'relative px-4 py-3 cursor-pointer group transition-colors',
                    'hover:bg-muted/60',
                    !notif.isRead && 'bg-primary/[0.04]'
                  )}
                  onClick={() => handleClick(notif)}
                >
                  {/* Indicateur unread (puce gauche) */}
                  {!notif.isRead && (
                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        'h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ring-1 ring-inset ring-border/40',
                        tone
                      )}
                    >
                      <SourceIcon className="h-3.5 w-3.5" strokeWidth={2.25} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'text-[13px] leading-snug line-clamp-2 tracking-tight',
                          !notif.isRead ? 'font-semibold text-foreground' : 'font-normal text-foreground/85'
                        )}
                      >
                        {notif.message}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-1 font-medium">
                        {formatDistanceToNow(new Date(notif.createdAt), {
                          addSuffix: true,
                          locale: fr,
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!notif.isRead && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 rounded-md hover:bg-background"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (notif.source === 'project') {
                              markProjectRead(notif.id);
                            } else if (notif.source === 'chat') {
                              markChatNotificationRead(notif.id).then(() => {
                                setChatNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
                                setChatUnreadCount(prev => Math.max(0, prev - 1));
                              });
                            } else if (notif.source === 'doc') {
                              markDocNotificationRead(notif.id).then(() => {
                                setDocNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
                                setDocUnreadCount(prev => Math.max(0, prev - 1));
                              });
                            }
                          }}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-md text-muted-foreground hover:bg-background hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notif);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
